import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  signal,
  ViewChild
} from '@angular/core';
import { Router } from '@angular/router';

import {
  NotificationFieldsFragment,
  NotificationKind
} from '@cpd-app/generated';
import { NotificationService } from '@cpd-app/core-module/services';

/**
 * What the list is showing. `empty` and `failed` are deliberately separate:
 * "you have no alerts" is a claim about the member's account and "we could not
 * read your alerts" is a claim about the request, and only one of them is safe
 * to make when the read did not come back.
 */
type DrawerState = 'loading' | 'failed' | 'empty' | 'rows';

/**
 * PWS 4.3.4. The shell's notification drawer.
 *
 * 00-foundations section 2: a row carries a title, a one-line body, a relative
 * timestamp, one destination and a dismiss. **Row types are visual only.** The
 * kind picks an icon and a grouping heading; where the row goes is per-row
 * data, so adding a fifth producer of notifications is a backend change and
 * nothing here.
 *
 * Grouped into "Waiting on you" and "For information": a reply or a school
 * response is something the member has to do something about, a completed
 * result or an agent signal is not, and eight undifferentiated rows made the
 * two indistinguishable.
 *
 * NOTHING PRODUCES A NOTIFICATION YET. `api.notification` has a read and a
 * dismiss, and no resolver, controller, cron job or AI service anywhere in
 * this repo inserts a row, so every member sees the empty state and will keep
 * seeing it until a producer is built. That producer is a feature and belongs
 * on its own ticket. What this component owes them in the meantime is an empty
 * state that says so plainly instead of one that reads like a feed that failed
 * or a load that never finished, which is what the `empty` state below is for.
 */
@Component({
  selector: 'cpd-notification-drawer',
  templateUrl: './notification-drawer.component.html',
  styleUrls: ['./notification-drawer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class NotificationDrawerComponent {
  /**
   * Focus follows the panel open and closed.
   *
   * `onKeydown` is bound on the <aside>, so Escape only ever reaches it when
   * focus is already inside: without this the keyboard user was left on the
   * bell behind the scrim and Escape did nothing. The panel only exists after
   * the @if renders it, hence the microtask before focusing.
   */
  @Input()
  public set isOpen(value: boolean) {
    const wasOpen = this.open;
    this.open = value;

    if (value && !wasOpen) {
      this.opener = document.activeElement as HTMLElement | null;
      Promise.resolve().then(() => this.focusPanel());
      return;
    }

    if (!value && wasOpen) {
      this.restoreFocus();
    }
  }

  public get isOpen(): boolean {
    return this.open;
  }

  @Output() public closed = new EventEmitter<void>();

  private open = false;
  /** Whatever had focus when the drawer opened, so closing puts it back. */
  protected opener: HTMLElement | null = null;

  @ViewChild('panel') private panel: ElementRef<HTMLElement>;

  public readonly notifications = this.notificationService.notifications;
  public readonly hasLoaded = this.notificationService.hasLoaded;
  public readonly loadFailed = this.notificationService.loadFailed;

  /**
   * The list as it stood when a retry began, or null when none is in flight.
   *
   * `refresh` clears `loadFailed` the moment it is called and leaves
   * `hasLoaded` set, so between the click and the answer the drawer's own
   * signals say "loaded, did not fail, no rows", which renders the empty state
   * over a read that has not come back. The service settles by assigning the
   * list, so a changed reference is the completion signal, whichever way it
   * went.
   */
  readonly #retryFrom = signal<readonly NotificationFieldsFragment[] | null>(
    null
  );

  private readonly isRetrying = computed(() => {
    const from = this.#retryFrom();

    return from !== null && this.notifications() === from;
  });

  /**
   * One state, named, rather than four conditions the template resolves in
   * order. `empty` and `failed` are the two that used to be indistinguishable,
   * and naming them is what lets a test assert which one a member is looking
   * at.
   */
  public readonly state = computed<DrawerState>(() => {
    if (!this.hasLoaded() || this.isRetrying()) {
      return 'loading';
    }

    if (this.loadFailed()) {
      return 'failed';
    }

    return this.notifications().length ? 'rows' : 'empty';
  });

  /** Rows a member is expected to act on. */
  public readonly waiting = computed(() =>
    this.notifications().filter((row) =>
      NotificationDrawerComponent.isWaiting(row)
    )
  );

  public readonly informational = computed(() =>
    this.notifications().filter(
      (row) => !NotificationDrawerComponent.isWaiting(row)
    )
  );

  constructor(
    private notificationService: NotificationService,
    private router: Router
  ) {}

  public close(): void {
    this.closed.emit();
  }

  /**
   * The failed state's only control.
   *
   * `refresh` and not `load`: `load` returns early once `hasLoaded` is set,
   * which it is on the failure path too, so it would do nothing here. The
   * once-only `load` the shell calls on open is untouched, and this is the
   * deliberate second ask the service already documents.
   */
  public retry(): void {
    this.#retryFrom.set(this.notifications());
    this.notificationService.refresh();
  }

  public onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      event.stopPropagation();
      this.close();
    }
  }

  public focusPanel(): void {
    this.panel?.nativeElement?.focus();
  }

  /**
   * Back to the control that opened the drawer, which is the bell in the
   * shell. Read off `document.activeElement` at open time rather than passed
   * in, so the drawer does not need to know who opened it.
   *
   * TODO: a focus trap (cdkTrapFocus) would keep Tab inside the panel while it
   * is open; this only fixes entering and leaving it.
   */
  private restoreFocus(): void {
    const opener = this.opener;
    this.opener = null;

    if (opener && document.contains(opener)) {
      opener.focus();
    }
  }

  public dismiss(row: NotificationFieldsFragment): void {
    this.notificationService.dismiss(row.id);
  }

  public goTo(row: NotificationFieldsFragment): void {
    if (!row.destinationPath) {
      return;
    }

    this.close();
    this.router.navigateByUrl(row.destinationPath);
  }

  public iconFor(kind: NotificationKind): string {
    switch (kind) {
      case NotificationKind.Reply:
        return 'comment';
      case NotificationKind.Action:
        return 'building';
      case NotificationKind.Result:
        return 'check-circle';
      default:
        return 'chart-line';
    }
  }

  /**
   * A relative time, because "2 days ago" is what a member wants from a drawer
   * and an absolute timestamp is what they want from a record. The exact time
   * stays on the title attribute for anyone who needs it.
   */
  public relativeTime(value: string | Date): string {
    const then = new Date(value).getTime();

    if (Number.isNaN(then)) {
      return '';
    }

    const minutes = Math.round((Date.now() - then) / 60_000);

    if (minutes < 1) {
      return 'just now';
    }
    if (minutes < 60) {
      return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
    }

    const hours = Math.round(minutes / 60);

    if (hours < 24) {
      return `${hours} hour${hours === 1 ? '' : 's'} ago`;
    }

    const days = Math.round(hours / 24);

    if (days < 30) {
      return `${days} day${days === 1 ? '' : 's'} ago`;
    }

    const months = Math.round(days / 30);

    return `${months} month${months === 1 ? '' : 's'} ago`;
  }

  private static isWaiting(row: NotificationFieldsFragment): boolean {
    return (
      row.kind === NotificationKind.Reply ||
      row.kind === NotificationKind.Action
    );
  }
}
